import React, { lazy, Suspense } from 'react';
import { Routes, Route, useLocation } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ImkerProvider } from './context/ImkerContext';
import { LanguageProvider } from './context/LanguageContext';
import { HelmetProvider } from 'react-helmet-async';
import './App.css';

import Header from './layouts/Header';
import Footer from './layouts/Footer';
import ErrorBoundary from './components/ErrorBoundary';
import LoadingSpinner from './components/LoadingSpinner';
import ScrollToTop from './components/ScrollToTop';
import { SPOTS_ENABLED } from './utils/spotsApi';
import HomePage from './pages/HomePage';

const ImkerDetailPage = lazy(() => import('./pages/ImkerDetailPage'));
const SearchResultsPage = lazy(() => import('./pages/SearchResultsPage'));
const RegionPage = lazy(() => import('./pages/RegionPage'));
const CategoryPage = lazy(() => import('./pages/CategoryPage'));
const CityPage = lazy(() => import('./pages/CityPage'));
const StatePage = lazy(() => import('./pages/StatePage'));
const CountryPage = lazy(() => import('./pages/CountryPage'));
const MapPage = lazy(() => import('./pages/MapPage'));
const ImprintPage = lazy(() => import('./pages/ImprintPage'));
const PrivacyPage = lazy(() => import('./pages/PrivacyPage'));
const AboutPage = lazy(() => import('./pages/AboutPage'));
const ContactPage = lazy(() => import('./pages/ContactPage'));
const FaqPage = lazy(() => import('./pages/FaqPage'));
const ForBeekeepersPage = lazy(() => import('./pages/ForBeekeepersPage'));
const ReportSpotPage = lazy(() => import('./pages/ReportSpotPage'));
const ReportedSpotPage = lazy(() => import('./pages/ReportedSpotPage'));
const ClaimSpotPage = lazy(() => import('./pages/ClaimSpotPage'));
const HoneyBatchPage = lazy(() => import('./pages/HoneyBatchPage'));
const NotFoundPage = lazy(() => import('./pages/NotFoundPage'));

// Erstelle einen QueryClient
const queryClient = new QueryClient({
    defaultOptions: {
        queries: {
            staleTime: 1000 * 60 * 30, // 30 Minuten
            gcTime: 1000 * 60 * 60, // 1 Stunde
        },
    },
});

function AppContent() {
    const location = useLocation();
    const isMapPage = location.pathname === '/map';

    return (
        <div className="App d-flex flex-column min-vh-100">
            <ScrollToTop />
            <Header />
            <main id="main-content" className={isMapPage ? '' : 'flex-grow-1'}>
                <ErrorBoundary>
                    <Suspense fallback={<LoadingSpinner />}>
                        <Routes>
                            <Route path="/" element={<HomePage />} />
                            <Route path="/imker-in/:country" element={<CountryPage />} />
                            <Route path="/imker-in/:country/:state" element={<StatePage />} />
                            <Route path="/imker-in/:country/:state/:city" element={<CityPage />} />
                            <Route path="/imker-in/:country/:state/:city/:id" element={<ImkerDetailPage />} />
                            <Route path="/search" element={<SearchResultsPage />} />
                            <Route path="/region/:land/:region" element={<RegionPage />} />
                            <Route path="/category/:categoryId" element={<CategoryPage />} />
                            <Route path="/map" element={<MapPage />} />
                            {SPOTS_ENABLED && (
                                <Route path="/melden" element={<ReportSpotPage />} />
                            )}
                            {SPOTS_ENABLED && (
                                <Route path="/gemeldet/:id" element={<ReportedSpotPage />} />
                            )}
                            {SPOTS_ENABLED && (
                                <Route path="/gemeldet/:id/uebernehmen" element={<ClaimSpotPage />} />
                            )}
                            <Route path="/h/:code" element={<HoneyBatchPage />} />
                            <Route path="/imprint" element={<ImprintPage />} />
                            <Route path="/privacy" element={<PrivacyPage />} />
                            <Route path="/about" element={<AboutPage />} />
                            <Route path="/contact" element={<ContactPage />} />
                            <Route path="/faq" element={<FaqPage />} />
                            <Route path="/for-beekeepers" element={<ForBeekeepersPage />} />
                            <Route path="*" element={<NotFoundPage />} />
                        </Routes>
                    </Suspense>
                </ErrorBoundary>
            </main>
            {!isMapPage && <Footer />}
        </div>
    );
}

function App() {
    return (
        <HelmetProvider>
            <QueryClientProvider client={queryClient}>
                <LanguageProvider>
                    <ImkerProvider>
                        <AppContent />
                    </ImkerProvider>
                </LanguageProvider>
            </QueryClientProvider>
        </HelmetProvider>
    );
}

export default App;
